import { createContext, useContext, useState, useEffect, useCallback, createElement } from 'react'
import type { ReactNode } from 'react'

interface User {
  email: string
  role: string
}

interface AuthState {
  user: User | null
  loading: boolean
  isAuthenticated: boolean
  isAdmin: boolean
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthState>({
  user: null,
  loading: true,
  isAuthenticated: false,
  isAdmin: false,
  logout: async () => {},
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function checkSession() {
      try {
        const res = await fetch('/api/auth/session', { credentials: 'include' })
        if (!res.ok) {
          setUser(null)
          return
        }
        const data = await res.json()
        if (!cancelled && data.authenticated) {
          setUser({ email: data.email, role: data.role })
        } else if (!cancelled) {
          setUser(null)
        }
      } catch {
        // Session check failed - treat as unauthenticated
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    checkSession()
    return () => { cancelled = true }
  }, [])

  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
    } catch {
      // Logout request failed - clear state anyway
    }
    setUser(null)
  }, [])

  const value: AuthState = {
    user,
    loading,
    isAuthenticated: user !== null,
    isAdmin: user?.role === 'admin',
    logout,
  }

  return createElement(AuthContext.Provider, { value }, children)
}

export function useAuth(): AuthState {
  return useContext(AuthContext)
}
